import { useEffect, useState } from "react";
import { Star, Plus, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const SEED_REVIEW = {
  id: "seed-steve-m",
  name: "Steve M.",
  rating: 5,
  location: "Melbourne, FL",
  comment:
    "Hurricane Cuts is a great name for this company. Ben and his crew swept in, cut all my palm trees, cleaned out all my boarder palm trees by the pool, cleaned up all the debris, and hauled away all the foliage. It looked like a different place after they finished up!",
  seed: true,
};

function StarRow({ rating, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "fill-[#FF5A00] text-[#FF5A00]" : "text-[#D1CFC7]"}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    rating: 5,
    comment: "",
    location: "",
  });

  const load = async () => {
    try {
      const [rRes, sRes] = await Promise.all([
        api.get("/reviews"),
        api.get("/reviews/stats"),
      ]);
      setReviews(rRes.data);
      setStats(sRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.comment || form.comment.length < 3) {
      toast.error("Please fill in your name and a review comment.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thanks — your review has been posted.");
      setOpen(false);
      setForm({ name: "", rating: 5, comment: "", location: "" });
      load();
    } catch (e) {
      toast.error("Could not submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const display = reviews.length > 0 ? reviews : [SEED_REVIEW];
  const avg = stats.count > 0 ? stats.average : 5.0;
  const count = stats.count > 0 ? stats.count : 1;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-[#F4F1EA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Word from the Yard</div>
            <h2 className="h-section text-[#0B1810]">
              Real Work. <span className="text-[#FF5A00]">Real Reviews.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end items-center gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-heading text-5xl text-[#0B1810]">
                  {avg.toFixed(1)}
                </span>
                <StarRow rating={Math.round(avg)} size={22} />
              </div>
              <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A] mt-1">
                {count} customer {count === 1 ? "review" : "reviews"}
              </div>
            </div>
            <button
              data-testid="open-review-form"
              onClick={() => setOpen(true)}
              className="btn-primary"
            >
              <Plus size={18} /> Leave a Review
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {display.map((r) => (
            <article
              key={r.id}
              data-testid="review-card"
              className="review-card"
            >
              <StarRow rating={r.rating} />
              <p className="mt-4 text-[#1A1A1A] font-body leading-relaxed">
                "{r.comment}"
              </p>
              <div className="mt-5 pt-4 border-t border-[#D1CFC7]">
                <div className="font-heading text-xl text-[#0B1810] tracking-wide">
                  {r.name}
                </div>
                {r.location && (
                  <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                    {r.location}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>

      {open && (
        <div
          data-testid="review-modal"
          className="fixed inset-0 z-[100] bg-[#0B1810]/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-[#F4F1EA] w-full max-w-lg border-2 border-[#0B1810] shadow-[10px_10px_0_#FF5A00] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="close-review-modal"
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 p-1 hover:bg-[#0B1810] hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="p-8">
              <div className="overline mb-2">Leave a Review</div>
              <h3 className="font-heading text-3xl md:text-4xl text-[#0B1810] mb-6">
                Tell us about the work.
              </h3>

              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="hc-label" htmlFor="review-name">
                    Your Name
                  </label>
                  <input
                    id="review-name"
                    data-testid="review-name-input"
                    className="hc-input"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="John D."
                    required
                  />
                </div>

                <div>
                  <label className="hc-label" htmlFor="review-location">
                    Location (optional)
                  </label>
                  <input
                    id="review-location"
                    data-testid="review-location-input"
                    className="hc-input"
                    type="text"
                    value={form.location}
                    onChange={(e) =>
                      setForm({ ...form, location: e.target.value })
                    }
                    placeholder="Palm Bay, FL"
                  />
                </div>

                <div>
                  <label className="hc-label">Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`rating-star-${i}`}
                        onClick={() => setForm({ ...form, rating: i })}
                        className="p-1"
                        aria-label={`${i} stars`}
                      >
                        <Star
                          size={32}
                          className={
                            i <= form.rating
                              ? "fill-[#FF5A00] text-[#FF5A00]"
                              : "text-[#D1CFC7]"
                          }
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="hc-label" htmlFor="review-comment">
                    Your Review
                  </label>
                  <textarea
                    id="review-comment"
                    data-testid="review-comment-input"
                    className="hc-textarea"
                    value={form.comment}
                    onChange={(e) =>
                      setForm({ ...form, comment: e.target.value })
                    }
                    placeholder="Ben and his crew were..."
                    required
                  />
                </div>

                <button
                  type="submit"
                  data-testid="review-submit-button"
                  disabled={submitting}
                  className="btn-primary w-full justify-center disabled:opacity-60"
                >
                  {submitting ? "Posting..." : "Post Review"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
